import {
  Button,
  Chip,
  Dialog,
  GrabIconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  newToast,
  useUnsavedChanges,
} from "@compass/core";
import { DataModelEnum } from "generated/openApi";
import { ReactNode, useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useDispatch } from "react-redux";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";

enum OrderMethod {
  Alphanumeric = "Alphanumeric",
  Manual = "Manual",
}

export const ReorderDataModels = <TListItem,>(props: {
  dataModelEnum: DataModelEnum;
  handle: string;
  getId: (t: TListItem) => number;
  getName: (t: TListItem) => string | undefined;
  getDisplayOrder: (t: TListItem) => number | undefined;
  fetch: () => Promise<TListItem[]>;
  display?: (t: TListItem) => ReactNode;
}) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [orderMethod, setOrderMethod] = useState<OrderMethod>();
  const [initOrderMethod, setInitOrderMethod] = useState<OrderMethod>();
  const [listItems, setListItems] = useState<TListItem[]>([]);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [listItems, orderMethod] });

  useEffect(() => {
    ApiService.common
      .usingDisplayOrder(props.dataModelEnum)
      .then((response) => setInitOrderMethod(response ? OrderMethod.Manual : OrderMethod.Alphanumeric));
  }, []);

  useEffect(() => {
    if (open) {
      props.fetch().then((listItems) => {
        const _orderMethod = listItems.some((x) => props.getDisplayOrder(x))
          ? OrderMethod.Manual
          : OrderMethod.Alphanumeric;
        setOrderMethod(_orderMethod);

        const _listItems = orderListItems(listItems, _orderMethod);
        setListItems(_listItems);

        resetUnsavedChanges([_listItems, orderMethod]);
      });
    }
  }, [open]);

  const orderListItems = (listItems: TListItem[], orderMethod: OrderMethod) => {
    const _listItems = Array.from(listItems);

    if (orderMethod === OrderMethod.Manual) {
      _listItems.sort((a, b) => {
        const aDisplayOrder = props.getDisplayOrder(a) || 0;
        const bDisplayOrder = props.getDisplayOrder(b) || 0;
        return aDisplayOrder - bDisplayOrder;
      });
    } else {
      _listItems.sort((a, b) => {
        const aName = props.getName(a) || "";
        const bName = props.getName(b) || "";
        return aName.localeCompare(bName);
      });
    }

    return _listItems;
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }

    const reorderedItems = Array.from(listItems);
    const [movedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, movedItem);
    setListItems(reorderedItems);
  };

  const save = () => {
    if (orderMethod === OrderMethod.Manual) {
      ApiService.common
        .assignDisplayOrders(
          props.dataModelEnum,
          listItems.map((x) => props.getId(x))
        )
        .then(() => {
          setInitOrderMethod(OrderMethod.Manual);
          setOpen(false);
          dispatch(setAppToast(newToast("success", "Order Updated")));
        });
    } else {
      ApiService.common.clearDisplayOrders(props.dataModelEnum).then(() => {
        setInitOrderMethod(OrderMethod.Alphanumeric);
        setOpen(false);
        dispatch(setAppToast(newToast("success", "Order Updated")));
      });
    }
  };

  return (
    <>
      {initOrderMethod && (
        <Tooltip
          title={
            initOrderMethod === OrderMethod.Manual
              ? "This list is currently ordered Manually"
              : "This list is currently ordered Alphanumerically"
          }
        >
          <Chip
            label={initOrderMethod === OrderMethod.Manual ? "M" : "A"}
            color={initOrderMethod === OrderMethod.Manual ? "secondary" : "success"}
            rectangular
            onClick={() => setOpen(true)}
          />
        </Tooltip>
      )}

      <Dialog
        open={open}
        maxWidth="xs"
        header={
          <Stack direction="row" justifyContent={"space-between"} alignItems="center">
            <Typography variant="inherit">{props.handle}</Typography>

            <Stack direction="row" spacing="sm">
              <Button onClick={() => setOpen(false)}>Close</Button>
              <Button onClick={() => save()} disabled={!unsavedChanges} variant="contained">
                Save
              </Button>
            </Stack>
          </Stack>
        }
        disableDrag
        content={
          <Stack spacing="sm">
            <Stack alignItems={"center"}>
              <Typography variant="caption">This order applies to all drop down boxes, tables and reports.</Typography>
            </Stack>

            <ToggleButtonGroup value={orderMethod} fullWidth>
              <ToggleButton
                value={OrderMethod.Alphanumeric}
                onClick={() => {
                  setOrderMethod(OrderMethod.Alphanumeric);
                  setListItems(orderListItems(listItems, OrderMethod.Alphanumeric));
                }}
                color="success"
                sx={{ textTransform: "none" }}
              >
                {OrderMethod.Alphanumeric} Order
              </ToggleButton>
              <ToggleButton
                value={OrderMethod.Manual}
                onClick={() => {
                  setOrderMethod(OrderMethod.Manual);
                  setListItems(orderListItems(listItems, OrderMethod.Manual));
                }}
                color="secondary"
                sx={{ textTransform: "none" }}
              >
                {OrderMethod.Manual} Order
              </ToggleButton>
            </ToggleButtonGroup>

            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId={"ReorderDataModels" + props.dataModelEnum.toString()}>
                {(provided) => (
                  <Stack spacing="sm">
                    <Stack ref={provided.innerRef} {...provided.droppableProps} spacing="sm">
                      {listItems.map((listItem, index) => {
                        const id = props.getId(listItem);
                        const name = props.getName(listItem);

                        return (
                          <Draggable key={id} draggableId={id.toString()} index={index}>
                            {(provided) => (
                              <Stack
                                key={id}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                direction="row"
                                py={1}
                                px={2}
                                border={1}
                                borderColor={(theme) => theme.palette.border.dark}
                                minHeight="50px"
                                justifyContent={"space-between"}
                                alignItems="center"
                              >
                                <Stack direction="row">
                                  <Typography fontSize="0.9rem">{name}</Typography>
                                </Stack>

                                {orderMethod === OrderMethod.Manual && <GrabIconButton {...provided.dragHandleProps} />}
                              </Stack>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </Stack>
                  </Stack>
                )}
              </Droppable>
            </DragDropContext>
          </Stack>
        }
      />
    </>
  );
};
